/**
 * What the journey's lender asks of the Work Details screen, read from its PROFILE FIELDS in
 * /public/lender-config (cpcore lender profile, lender4 P9):
 *   capabilities.employment.workEmailRequired   — false: no work email input, no OTP step
 *   capabilities.employment.selfEmployedAllowed — true: self-employed tenants are acceptable
 *   limits.exposureCapInr                       — the lender's loan cap, in rupees
 *
 * Generic on purpose: it never looks at the lender key, only at what the profile says. Every
 * field that is absent (an older backend, a lender that does not set it, a config that has not
 * loaded, a journey whose lender is not known yet) means TODAY'S behaviour — work email asked and
 * verified, the ₹2,00,000 cap. Only an explicit `false` / `true` / positive number changes anything.
 *
 * Dependency-free so node:test covers it: node --test src/b2b/lib/employmentRules.test.js
 */

/** Today's cap (cpcore credit-path exposure cap, also cpbase creditPath.js EXPOSURE_CAP). */
export const DEFAULT_EXPOSURE_CAP_INR = 200000;

export const DEFAULT_EMPLOYMENT_RULES = Object.freeze({
  workEmailRequired: true,
  selfEmployedAllowed: false,
  exposureCapInr: DEFAULT_EXPOSURE_CAP_INR,
  hiddenEmploymentTypes: Object.freeze([]),
  known: false,
});

/** SALARIED is never hidden: every lender takes it, and the screen must always offer something. */
const HIDEABLE_EMPLOYMENT_TYPES = ["SELF_EMPLOYED", "INTERN"];

function hiddenTypesOf(value) {
  if (!Array.isArray(value)) return [];
  return value.filter((t) => typeof t === "string" && HIDEABLE_EMPLOYMENT_TYPES.includes(t));
}

/**
 * The rules for `key`, read from the live lender profiles (`lenderConfig.lenders`).
 * Returns DEFAULT_EMPLOYMENT_RULES (today's behaviour) when the key or its profile is unknown.
 */
export function employmentRulesFor(lenders, key) {
  const k = typeof key === "string" ? key.trim() : "";
  const profile = k && lenders && typeof lenders === "object" ? lenders[k] : null;
  if (!profile || typeof profile !== "object") return DEFAULT_EMPLOYMENT_RULES;
  const employment = profile.capabilities?.employment ?? {};
  const cap = profile.limits?.exposureCapInr;
  return {
    workEmailRequired: employment.workEmailRequired !== false,
    selfEmployedAllowed: employment.selfEmployedAllowed === true,
    exposureCapInr:
      typeof cap === "number" && Number.isFinite(cap) && cap > 0 ? cap : DEFAULT_EXPOSURE_CAP_INR,
    hiddenEmploymentTypes: hiddenTypesOf(employment.hiddenEmploymentTypes),
    known: true,
  };
}

/**
 * The employment-type tabs to show: `types` minus the ones the lender hides
 * (`capabilities.employment.hiddenEmploymentTypes`). Never empty — if everything would be hidden,
 * every type is shown, as today.
 */
export function offeredEmploymentTypes(types, rules = DEFAULT_EMPLOYMENT_RULES) {
  const hidden = rules?.hiddenEmploymentTypes ?? [];
  const offered = types.filter((t) => !hidden.includes(t.id));
  return offered.length ? offered : types;
}

/**
 * The type the form works with: the tenant's choice while it is offered, else the first offered
 * type. Derived on every render, so a renewal pre-fill of a hidden type, or a lender that is known
 * only after the first render, can never leave a hidden type selected — and after a fallback to a
 * lender that offers it again, the tenant's own choice comes back.
 */
export function activeEmploymentType(chosen, offered) {
  return offered.some((t) => t.id === chosen) ? chosen : offered[0]?.id ?? chosen;
}

/** The salaried form fields, without the Work Email input when the lender does not ask for one. */
export function salariedFields(fields, rules = DEFAULT_EMPLOYMENT_RULES) {
  if (rules.workEmailRequired) return fields;
  return fields.filter((f) => f?.name !== "workEmail");
}

/** Fields that must be filled before the form can be submitted. Salary is required for every lender. */
export function requiredWorkFields(employmentType, rules = DEFAULT_EMPLOYMENT_RULES) {
  if (employmentType === "SALARIED") {
    const base = ["companyName", "designation", "officeAddress"];
    return rules.workEmailRequired ? [...base, "workEmail", "income"] : [...base, "income"];
  }
  if (employmentType === "SELF_EMPLOYED") return ["businessName", "turnover", "registrationYear"];
  return [""];
}

/**
 * The monthly salary as plain digits ("45,000" / "45 000" → "45000"), or null when it is not a
 * whole number above zero ("12 LPA", "4.5 lakh", "0", empty). Tenants typed free text into this
 * field before it was validated, and a renewal pre-fills that old value, so it is checked here.
 */
export function normaliseSalary(value) {
  if (value === null || value === undefined) return null;
  const digits = String(value).replace(/[,\s]/g, "");
  if (!/^[0-9]+$/.test(digits)) return null;
  const trimmed = digits.replace(/^0+/, "");
  return trimmed === "" ? null : trimmed;
}

/** True when the screen must collect AND verify (OTP) a work email for this submission. */
export function needsWorkEmailOtp(employmentType, rules = DEFAULT_EMPLOYMENT_RULES) {
  return employmentType === "SALARIED" && rules.workEmailRequired;
}

/**
 * The body for `user/work-details`. When the lender asks for no work email, a work email left in
 * the form (e.g. pre-filled from a previous journey) is NOT sent: it was never verified on this
 * journey, and sending it would store an unverified address as this journey's work email.
 */
export function workDetailsPayload(formData, employmentType, rules = DEFAULT_EMPLOYMENT_RULES) {
  const body = { ...(formData || {}), employmentType };
  if (!rules.workEmailRequired) delete body.workEmail;
  if (employmentType === "SALARIED" && normaliseSalary(body.income)) body.income = normaliseSalary(body.income);
  return body;
}

/**
 * Whether the journey's work-details step is done, for the routing gates (HomeScreen,
 * OnboardingSteps). Today: a work email on file, or a self-employed record. When the lender asks
 * for no work email, any submitted employment record counts.
 */
export function isWorkDetailsDone(employment, rules = DEFAULT_EMPLOYMENT_RULES) {
  if (!employment) return false;
  if (employment.workEmail || employment.employmentType === "SELF_EMPLOYED") return true;
  return !rules.workEmailRequired && Boolean(employment.employmentType);
}
